import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ConfirmDialog from "../components/ConfirmDialog";
import useToast from "../hooks/use-toast-admin";
import {
  UserPlus,
  Trash,
  Key,
  Check,
  X,
  ShieldCheck,
  PencilSimple,
} from "@phosphor-icons/react";

const emptyForm = { email: "", name: "", password: "", role: "editor" };

const Badge = ({ children, color }) => (
  <span
    className="inline-block px-2.5 py-1 rounded-full text-[0.62rem] uppercase tracking-[0.18em] font-nav font-semibold"
    style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}
  >
    {children}
  </span>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-ZA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [confirmTarget, setConfirmTarget] = useState(null); // { u, busy, err }
  const [resetTarget, setResetTarget] = useState(null); // { u, password, busy, err }
  const { showToast, ToastView } = useToast();

  const load = () =>
    api
      .get("/admin/users")
      .then((r) => setItems(r.data || []))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const seedEmail = useMemo(
    () => items.find((u) => u.role === "admin")?.email, // best-effort hint for UI
    [items]
  );

  const submitCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (form.password.length < 8) {
      setErr("Password must be at least 8 characters");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await api.post("/admin/users", form);
      setForm(emptyForm);
      setCreating(false);
      await load();
      showToast("ok", `Invited ${form.email}.`);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { active: !u.active });
      await load();
      showToast("ok", `${u.email} ${!u.active ? "reactivated" : "deactivated"}.`);
    } catch (e) {
      showToast("err", formatApiError(e));
    }
  };

  const flipRole = async (u) => {
    const next = u.role === "admin" ? "editor" : "admin";
    try {
      await api.put(`/admin/users/${u.id}`, { role: next });
      await load();
      showToast("ok", `${u.email} → ${next}.`);
    } catch (e) {
      showToast("err", formatApiError(e));
    }
  };

  const performDelete = async () => {
    if (!confirmTarget?.u) return;
    setConfirmTarget((c) => ({ ...c, busy: true, err: "" }));
    try {
      await api.delete(`/admin/users/${confirmTarget.u.id}`);
      const removed = confirmTarget.u;
      setConfirmTarget(null);
      await load();
      showToast("ok", `Removed ${removed.email}.`);
    } catch (e) {
      setConfirmTarget((c) => ({ ...c, busy: false, err: formatApiError(e) }));
    }
  };

  const performReset = async () => {
    if (!resetTarget?.u) return;
    if ((resetTarget.password || "").length < 8) {
      setResetTarget((r) => ({ ...r, err: "Password must be at least 8 characters" }));
      return;
    }
    setResetTarget((r) => ({ ...r, busy: true, err: "" }));
    try {
      await api.post(`/admin/users/${resetTarget.u.id}/reset-password`, {
        password: resetTarget.password,
      });
      const email = resetTarget.u.email;
      setResetTarget(null);
      showToast("ok", `Password reset for ${email}.`);
    } catch (e) {
      setResetTarget((r) => ({ ...r, busy: false, err: formatApiError(e) }));
    }
  };

  return (
    <div className="p-10" data-testid="admin-users-page">
      <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
        <div>
          <div className="overline text-[#a8b2cc] mb-3">Access Control</div>
          <h1 className="font-display text-5xl tracking-tighter">Team</h1>
          <p className="mt-3 text-[#a8b2cc] max-w-xl">
            Invite staff, manage roles, and rotate passwords. Editors can manage
            offers, slides, and enquiries — only admins can access this page and
            the Login Activity log.
          </p>
        </div>
        <button
          onClick={() => setCreating((c) => !c)}
          className="btn-brutalist"
          data-testid="open-invite-form"
        >
          <UserPlus size={16} weight="bold" /> {creating ? "Close form" : "Invite user"}
        </button>
      </div>

      {err && !creating && (
        <div
          className="border border-[#af3fa6] text-[#af3fa6] p-3 text-sm mb-6"
          data-testid="users-error"
        >
          {err}
        </div>
      )}

      {creating && (
        <form
          onSubmit={submitCreate}
          className="rounded-[24px] border border-[#2c3854] bg-[#141e32] p-6 mb-8 grid grid-cols-1 md:grid-cols-5 gap-4"
          data-testid="invite-form"
        >
          <label className="md:col-span-2 block">
            <span className="block font-mono-accent text-[0.7rem] uppercase tracking-[0.22em] text-[#a8b2cc] mb-2">
              Email
            </span>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value.trim() })}
              className="admin-input"
              data-testid="invite-email"
            />
          </label>
          <label className="md:col-span-2 block">
            <span className="block font-mono-accent text-[0.7rem] uppercase tracking-[0.22em] text-[#a8b2cc] mb-2">
              Full name
            </span>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="admin-input"
              data-testid="invite-name"
            />
          </label>
          <label className="md:col-span-1 block">
            <span className="block font-mono-accent text-[0.7rem] uppercase tracking-[0.22em] text-[#a8b2cc] mb-2">
              Role
            </span>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="admin-input"
              data-testid="invite-role"
            >
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <label className="md:col-span-3 block">
            <span className="block font-mono-accent text-[0.7rem] uppercase tracking-[0.22em] text-[#a8b2cc] mb-2">
              Initial password (min 8 chars)
            </span>
            <input
              type="text"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Share this securely out-of-band"
              className="admin-input"
              data-testid="invite-password"
            />
          </label>
          <div className="md:col-span-2 flex items-end gap-3">
            <button
              type="submit"
              disabled={saving}
              className="btn-brutalist flex-1 justify-center disabled:opacity-60"
              data-testid="invite-submit"
            >
              <Check size={14} weight="bold" />
              {saving ? "Saving…" : "Create user"}
            </button>
          </div>
          {err && (
            <div
              className="md:col-span-5 border border-[#af3fa6] text-[#af3fa6] p-3 text-sm"
              data-testid="invite-error"
            >
              {err}
            </div>
          )}
        </form>
      )}

      {loading ? (
        <div className="text-[#a8b2cc] font-mono-accent">Loading…</div>
      ) : (
        <div className="rounded-[24px] border border-[#2c3854] bg-[#141e32] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono-accent text-[0.65rem] uppercase tracking-[0.22em] text-[#a8b2cc] border-b border-[#2c3854]">
                <th className="p-4">User</th>
                <th className="p-4">Role</th>
                <th className="p-4">Status</th>
                <th className="p-4">Last sign-in</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const isMe = u.id === me?.id;
                const isSeed =
                  u.email ===
                  (items.find((x) => x.role === "admin" && x.email?.startsWith("admin@"))?.email ||
                    seedEmail);
                return (
                  <tr
                    key={u.id}
                    className="border-b border-[#2c3854] last:border-0"
                    data-testid={`user-row-${u.id}`}
                  >
                    <td className="p-4">
                      <div className="text-[#f5ecd9] font-display text-base tracking-tight">
                        {u.name || "—"}
                      </div>
                      <div className="text-[#a8b2cc] text-xs mt-0.5">{u.email}</div>
                      {isMe && (
                        <Badge color="#55a2d2">You</Badge>
                      )}
                    </td>
                    <td className="p-4">
                      {u.role === "admin" ? (
                        <Badge color="#f5ecd9">Admin</Badge>
                      ) : (
                        <Badge color="#dda632">Editor</Badge>
                      )}
                    </td>
                    <td className="p-4">
                      {u.active ? (
                        <Badge color="#55a2d2">Active</Badge>
                      ) : (
                        <Badge color="#af3fa6">Deactivated</Badge>
                      )}
                    </td>
                    <td className="p-4 text-[#a8b2cc] font-mono-accent text-xs">
                      {fmtDate(u.last_login_at)}
                    </td>
                    <td className="p-4 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => flipRole(u)}
                          disabled={isMe || isSeed}
                          className="p-2 border border-[#2c3854] text-[#a8b2cc] hover:border-[#f5ecd9] hover:text-[#f5ecd9] rounded-full disabled:opacity-30 disabled:cursor-not-allowed"
                          title={
                            isSeed
                              ? "Seed admin role is locked"
                              : isMe
                              ? "You can't change your own role"
                              : u.role === "admin"
                              ? "Demote to editor"
                              : "Promote to admin"
                          }
                          data-testid={`user-flip-role-${u.id}`}
                        >
                          <ShieldCheck size={14} weight="bold" />
                        </button>
                        <button
                          onClick={() => toggleActive(u)}
                          disabled={isMe || isSeed}
                          className="p-2 border border-[#2c3854] text-[#a8b2cc] hover:border-[#dda632] hover:text-[#dda632] rounded-full disabled:opacity-30 disabled:cursor-not-allowed"
                          title={
                            isSeed
                              ? "Seed admin can't be deactivated"
                              : isMe
                              ? "You can't deactivate yourself"
                              : u.active
                              ? "Deactivate"
                              : "Reactivate"
                          }
                          data-testid={`user-toggle-active-${u.id}`}
                        >
                          {u.active ? <X size={14} weight="bold" /> : <Check size={14} weight="bold" />}
                        </button>
                        <button
                          onClick={() => setResetTarget({ u, password: "", busy: false, err: "" })}
                          className="p-2 border border-[#2c3854] text-[#a8b2cc] hover:border-[#55a2d2] hover:text-[#55a2d2] rounded-full"
                          title="Reset password"
                          data-testid={`user-reset-pw-${u.id}`}
                        >
                          <Key size={14} weight="bold" />
                        </button>
                        <button
                          onClick={() => setConfirmTarget({ u, busy: false, err: "" })}
                          disabled={isMe || isSeed}
                          className="p-2 border border-[#af3fa6] text-[#af3fa6] hover:bg-[#af3fa6] hover:text-white rounded-full disabled:opacity-30 disabled:cursor-not-allowed"
                          title={
                            isSeed
                              ? "Seed admin can't be removed"
                              : isMe
                              ? "You can't remove yourself"
                              : "Remove user"
                          }
                          data-testid={`user-delete-${u.id}`}
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmTarget}
        title="Remove this user?"
        description={
          confirmTarget?.u ? (
            <>
              Permanently removes <span className="text-[#f5ecd9]">{confirmTarget.u.email}</span>.
              They will no longer be able to sign in. Their past activity stays
              in the audit log.
            </>
          ) : null
        }
        confirmLabel="Yes, remove"
        onCancel={() => setConfirmTarget(null)}
        onConfirm={performDelete}
        busy={confirmTarget?.busy}
        error={confirmTarget?.err}
        testId="user-confirm-delete"
      />

      {resetTarget && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6"
          onClick={() => !resetTarget.busy && setResetTarget(null)}
        >
          <div
            className="bg-[#141e32] border border-[#2c3854] rounded-[24px] w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="reset-pw-modal"
          >
            <div className="p-6 border-b border-[#2c3854]">
              <div className="overline text-[#a8b2cc] mb-1">Reset password</div>
              <h3 className="font-display text-2xl tracking-tighter text-[#f5ecd9]">
                {resetTarget.u.email}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="block font-mono-accent text-[0.7rem] uppercase tracking-[0.22em] text-[#a8b2cc] mb-2">
                  New password (min 8 chars)
                </span>
                <input
                  type="text"
                  autoFocus
                  value={resetTarget.password}
                  onChange={(e) =>
                    setResetTarget((r) => ({ ...r, password: e.target.value, err: "" }))
                  }
                  className="admin-input"
                  data-testid="reset-pw-input"
                />
              </label>
              {resetTarget.err && (
                <div className="text-[#af3fa6] text-sm" data-testid="reset-pw-error">
                  {resetTarget.err}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-[#2c3854] flex gap-3 justify-end">
              <button
                onClick={() => setResetTarget(null)}
                className="btn-ghost"
                disabled={resetTarget.busy}
                data-testid="reset-pw-cancel"
              >
                Cancel
              </button>
              <button
                onClick={performReset}
                disabled={resetTarget.busy || !resetTarget.password}
                className="btn-brutalist disabled:opacity-60"
                data-testid="reset-pw-submit"
              >
                <PencilSimple size={14} weight="bold" />
                {resetTarget.busy ? "Saving…" : "Save new password"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastView />
    </div>
  );
}
